import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  AlertTitle,
  Stack,
  Button,
  Typography,
  Chip,
  Autocomplete,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Divider,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell
} from '@mui/material';
import {
  Add as AddIcon,
  Payment as PaymentIcon
} from '@mui/icons-material';
import {
  PayrollHeader,
  PayrollSummaryCards,
  PayrollTable,
  CompletedWorkTable,
  PayrollTabs,
  TabPanel,
  GeneratePayrollDialog,
  PayrollDetailsDialog,
  BulkActionsDialog,
  PayrollFilters,
  PayrollStatusAlerts,
  ApprovalDialog,
  PaymentDialog,
  RejectDialog,
  EditPayrollDialog,
  WeeklyPayrollDialog,
  ExportDialog,
  ShiftEditApprovalDialog
} from '../../components/payroll';

// Define the type locally
interface EditableShiftData {
  actualStartTime: string;
  actualEndTime: string;
  hourlyRate: number;
  notes: string;
  totalHours: number;
}
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import PageLayout from '../../components/Layout/PageLayout';
import { useAuth } from '../../contexts/AuthContext';
import { handleApiError } from '../../utils/errorUtils';
import { 
  payrollService, 
  StaffPayrollDto, 
  PayrollStatus,
  PayrollFrequency,
  GeneratePayrollRequest,
  AdminApprovalRequest,
  PaymentProcessRequest,
  PayrollRejectRequest,
  PaymentMethod,
  PayrollSummary,
  WeeklyPayrollRequest,
  CompletedWorkItem,
  ApproveCompletedWorkRequest
} from '../../services/payrollService';

const PayrollPage: React.FC = () => {
  const { user } = useAuth();
  
  // State management
  const [activeTab, setActiveTab] = useState(0);
  const [payrolls, setPayrolls] = useState<StaffPayrollDto[]>([]);
  const [readyForPayment, setReadyForPayment] = useState<StaffPayrollDto[]>([]);
  const [paidPayrolls, setPaidPayrolls] = useState<StaffPayrollDto[]>([]);
  const [completedWork, setCompletedWork] = useState<CompletedWorkItem[]>([]);
  const [summary, setSummary] = useState<PayrollSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Selection state for bulk operations
  const [selectedPayrolls, setSelectedPayrolls] = useState<number[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  
  // Dialog states
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [quickWeeklyDialogOpen, setQuickWeeklyDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [shiftEditDialogOpen, setShiftEditDialogOpen] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailTarget, setEmailTarget] = useState<StaffPayrollDto | null>(null);
  const [emailAddress, setEmailAddress] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [selectedPayroll, setSelectedPayroll] = useState<StaffPayrollDto | null>(null);
  const [selectedShift, setSelectedShift] = useState<CompletedWorkItem | null>(null);
  
  // Menu state
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuPayroll, setMenuPayroll] = useState<StaffPayrollDto | null>(null);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<PayrollStatus | ''>('');
  const [dateRange, setDateRange] = useState<{ start: Date | null; end: Date | null }>({
    start: null,
    end: null
  });

  // Form states
  const [generateForm, setGenerateForm] = useState<GeneratePayrollRequest>({
    staffIds: [],
    startDate: '',
    endDate: '',
    payrollFrequency: PayrollFrequency.Weekly,
    overtimeThreshold: 40,
    overtimeMultiplier: 1.5,
    includeOnlyCompletedShifts: true
  });
  
  const [weeklyForm, setWeeklyForm] = useState<WeeklyPayrollRequest>({
    staffIds: [],
    weekStart: '',
    overtimeThreshold: 40,
    overtimeMultiplier: 1.5,
    includeOnlyCompletedShifts: true
  });
  
  const [editForm, setEditForm] = useState<Partial<StaffPayrollDto>>({});
  
  const [approvalForm, setApprovalForm] = useState<AdminApprovalRequest>({
    approvedBy: user?.id || 0,
    approvalNotes: ''
  });
  
  const [paymentForm, setPaymentForm] = useState<PaymentProcessRequest>({
    paymentMethod: PaymentMethod.DirectDeposit,
    transactionId: '',
    paymentReference: '',
    paymentNotes: ''
  });
  
  const [rejectForm, setRejectForm] = useState<PayrollRejectRequest>({
    rejectedBy: user?.id || 0,
    rejectionReason: '',
    rejectionNotes: ''
  });

  const [bulkOperation, setBulkOperation] = useState<'approve' | 'reject' | 'delete' | ''>('');
  const [bulkForm, setBulkForm] = useState<{
    action: 'approve' | 'reject' | 'markPaid' | 'delete' | '';
    reason?: string;
  }>({
    action: '',
    reason: ''
  });
  const [exportFormat, setExportFormat] = useState<'csv' | 'pdf' | 'excel'>('csv');
  
  // Completed work selection state
  const [selectedWork, setSelectedWork] = useState<number[]>([]);
  const [payrollReviewDialogOpen, setPayrollReviewDialogOpen] = useState(false);
  // Review dialog state (must be at component level — hooks cannot be inside callbacks/IIFEs)
  const [reviewFrequency, setReviewFrequency] = useState<PayrollFrequency>(PayrollFrequency.Weekly);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewSelectedIds, setReviewSelectedIds] = useState<number[]>([]);

  // Helper function to check permissions.
  // Roles are matched case-insensitively because the auth backend returns
  // canonical-cased values ("Admin", "Manager") while callers historically
  // used lowercase strings.
  const hasPermission = (role: string): boolean => {
    const roles = (user?.roles ?? []).map((r) => r.toLowerCase());
    const target = role.toLowerCase();
    if (roles.includes(target)) return true;
    if (roles.includes('admin') || roles.includes('administrator')) return true;
    if (roles.includes('manager')) return true;
    // user_type is the legacy field — fall back to it when roles[] is empty
    const ut = (user?.user_type || '').toLowerCase();
    return ut === 'admin' || ut === 'administrator' || ut === 'manager';
  };

  useEffect(() => {
    loadData();
  }, [activeTab]);

  // Sync reviewSelectedIds whenever the review dialog opens
  useEffect(() => {
    if (payrollReviewDialogOpen) {
      setReviewSelectedIds(selectedWork);
    }
  }, [payrollReviewDialogOpen]);

  const loadData = async () => {
    setLoading(true);
    setError('');

    try {
      const [payrollsResponse, summaryResponse, completedWorkResponse] = await Promise.all([
        payrollService.getPayrolls({}),
        payrollService.getPayrollSummary(),
        payrollService.getCompletedWork({
          startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // Last 30 days
          endDate: new Date().toISOString().split('T')[0]
        })
      ]);

      if (payrollsResponse.success) {
        const all = payrollsResponse.data || [];
        setPayrolls(all);
        setPaidPayrolls(all.filter(p => p.status === PayrollStatus.Paid));
        setReadyForPayment(all.filter(p => p.status !== PayrollStatus.Paid && p.status !== PayrollStatus.Rejected));
      }

      if (summaryResponse.success && summaryResponse.data) {
        setSummary(summaryResponse.data);
      }

      if (completedWorkResponse.success && completedWorkResponse.data) {
        // Cross-reference included shift IDs so we can show "payroll generated" status
        const includedShiftIdsResponse = await payrollService.getIncludedShiftIds();
        const includedIds = new Set<number>(includedShiftIdsResponse.data || []);

        // Also check existing payrolls for their included shift IDs in case the API isn't available
        const allPayrolls = payrollsResponse.data || [];
        allPayrolls.forEach(p => {
          (p as any).includedShiftIds?.forEach((id: number) => includedIds.add(id));
        });

        const markedWork = completedWorkResponse.data.map(work => {
          const payrollForShift = allPayrolls.find(p =>
            ((p as any).includedShiftIds as number[] | undefined)?.includes(work.id)
          );
          return {
            ...work,
            payrollGenerated: includedIds.has(work.id),
            payrollId: payrollForShift?.id
          };
        });
        setCompletedWork(markedWork);
      }
    } catch (err) {
      handleApiError(err, setError);
    } finally {
      setLoading(false);
    }
  };

  // Filter and search functionality
  const getFilteredData = (data: StaffPayrollDto[]) => {
    return data.filter(payroll => {
      const matchesSearch = searchTerm === '' || 
        payroll.staffName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        payroll.employeeId.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = statusFilter === '' || payroll.status === statusFilter;
      
      const matchesDateRange = (!dateRange.start || new Date(payroll.payrollStartDate) >= dateRange.start) &&
        (!dateRange.end || new Date(payroll.payrollEndDate) <= dateRange.end);
      
      return matchesSearch && matchesStatus && matchesDateRange;
    });
  };

  // Event handlers
  const handleGeneratePayrolls = async () => {
    setLoading(true);
    try {
      let formToSubmit = { ...generateForm };
      
      // If "Include Only Completed Shifts" is checked and staffIds is empty, 
      // fetch completed shifts and extract staff IDs
      if (generateForm.includeOnlyCompletedShifts && (!generateForm.staffIds || generateForm.staffIds.length === 0)) {
        const completedWorkResponse = await payrollService.getCompletedWork({
          startDate: generateForm.startDate,
          endDate: generateForm.endDate
        });
        
        if (completedWorkResponse.success && completedWorkResponse.data) {
          // Extract unique staff IDs from completed shifts
          const uniqueStaffIds = [...new Set(completedWorkResponse.data.map(shift => shift.staffId))];
          formToSubmit.staffIds = uniqueStaffIds;
        }
      }
      
      const response = await payrollService.generatePayrolls(formToSubmit);
      if (response.success) {
        setSuccess(`Generated ${response.data?.length || 0} payrolls successfully`);
        setGenerateDialogOpen(false);
        loadData();
      } else {
        setError(response.message || 'Failed to generate payrolls');
      }
    } catch (err) {
      handleApiError(err, setError);
    } finally {
      setLoading(false);
    }
  };

  // Opens the review dialog pre-selecting all unprocessed completed works (used by the main Generate button)
  const handleOpenGenerateReview = () => {
    const unprocessed = completedWork.filter(w => !w.payrollGenerated).map(w => w.id);
    setSelectedWork(unprocessed);
    setReviewSelectedIds(unprocessed);
    setPayrollReviewDialogOpen(true);
  };

  // Called from review dialog — generates payrolls for the confirmed shift selection
  const handleConfirmGeneratePayrolls = async () => {
    const reviewItems = completedWork.filter(w => reviewSelectedIds.includes(w.id));
    if (reviewItems.length === 0) return;
    const startDate = reviewItems.reduce((min, w) => w.shiftDate < min ? w.shiftDate : min, reviewItems[0].shiftDate);
    const endDate   = reviewItems.reduce((max, w) => w.shiftDate > max ? w.shiftDate : max, reviewItems[0].shiftDate);

    // Build staff details map so the backend doesn't need to call the staff
    // service. We intentionally do NOT pass `hourlyRate` here — payroll rates
    // come from Billing Rates (StaffBaseRate × StaffPay segment %), which the
    // backend resolves directly from the database.
    const staffDetails: Record<number, { staffName: string; employeeId: string }> = {};
    reviewItems.forEach(item => {
      staffDetails[item.staffId] = {
        staffName: item.staffName,
        employeeId: item.employeeId,
      };
    });

    setReviewLoading(true);
    try {
      const request: GeneratePayrollRequest = {
        shiftIds: reviewSelectedIds,
        startDate,
        endDate,
        payrollFrequency: reviewFrequency,
        overtimeThreshold: 40,
        overtimeMultiplier: 1.5,
        includeOnlyCompletedShifts: true,
        staffDetails,
      };
      const response = await payrollService.generatePayrolls(request);
      if (response.success) {
        const generated = response.data || [];
        setPayrollReviewDialogOpen(false);
        setSelectedWork([]);
        await loadData();
        // Auto-open the view dialog for the first generated payroll
        if (generated.length > 0) {
          setSelectedPayroll(generated[0]);
          setViewDialogOpen(true);
        }
        const byStaffCount = new Set(reviewItems.map(i => i.staffId)).size;
        setSuccess(`Generated ${generated.length} payroll(s) for ${byStaffCount} employee(s)`);
      } else {
        setError(response.message || 'Failed to generate payrolls');
      }
    } catch (err) {
      handleApiError(err, setError);
    } finally {
      setReviewLoading(false);
    }
  };


  const handleQuickWeeklyPayrolls = async () => {
    setLoading(true);
    try {
      const response = await payrollService.generateWeeklyPayrolls(weeklyForm);
      if (response.success) {
        setSuccess(`Generated ${response.data?.length || 0} weekly payrolls successfully`);
        setQuickWeeklyDialogOpen(false);
        loadData();
      } else {
        setError(response.message || 'Failed to generate weekly payrolls');
      }
    } catch (err) {
      handleApiError(err, setError);
    } finally {
      setLoading(false);
    }
  };

  const handleViewPayroll = (payroll: StaffPayrollDto) => {
    setSelectedPayroll(payroll);
    setViewDialogOpen(true);
  };

  const handleEditPayroll = (payroll: StaffPayrollDto) => {
    setSelectedPayroll(payroll);
    setEditForm({
      grossPay: payroll.grossPay,
      netPay: payroll.netPay,
      regularHours: payroll.regularHours,
      overtimeHours: payroll.overtimeHours,
      hourlyRate: payroll.hourlyRate
    });
    setEditDialogOpen(true);
  };

  const handleDeletePayroll = async (payroll: StaffPayrollDto) => {
    setLoading(true);
    try {
      const response = await payrollService.deletePayroll(payroll.id);
      if (response.success) {
        setSuccess(`Payroll #${payroll.id} deleted successfully`);
        loadData();
      } else {
        setError(response.message || 'Failed to delete payroll');
      }
    } catch (err) {
      handleApiError(err, setError);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenEmailDialog = (payroll: StaffPayrollDto) => {
    setEmailTarget(payroll);
    setEmailAddress('');
    setEmailDialogOpen(true);
  };

  const handleSendEmail = async () => {
    if (!emailTarget) return;
    if (!emailAddress || !/\S+@\S+\.\S+/.test(emailAddress)) {
      setError('Please enter a valid email address');
      return;
    }
    setEmailSending(true);
    try {
      const response = await payrollService.sendPayrollEmail(emailTarget.id, emailAddress);
      if (response.success) {
        setSuccess(response.message || `Payroll #${emailTarget.id} emailed to ${emailAddress}`);
        setEmailDialogOpen(false);
      } else {
        setError(response.message || 'Failed to send email');
      }
    } catch (err) {
      handleApiError(err, setError);
    } finally {
      setEmailSending(false);
    }
  };

  const handleBulkOperation = async () => {
    if (selectedPayrolls.length === 0) {
      setError('Please select payrolls to perform bulk operation');
      return;
    }

    setLoading(true);
    try {
      const promises = selectedPayrolls.map(async (payrollId) => {
        switch (bulkOperation) {
          case 'approve':
            return payrollService.adminApprovePayroll(payrollId, approvalForm);
          case 'reject':
            return payrollService.rejectPayroll(payrollId, rejectForm);
          case 'delete':
            return payrollService.deletePayroll(payrollId);
          default:
            return Promise.resolve({ success: false });
        }
      });

      const results = await Promise.all(promises);
      const successCount = results.filter(r => r.success).length;
      
      if (successCount > 0) {
        setSuccess(`Successfully ${bulkOperation}d ${successCount} payroll(s)`);
        setBulkDialogOpen(false);
        setSelectedPayrolls([]);
        loadData();
      } else {
        setError(`Failed to ${bulkOperation} payrolls`);
      }
    } catch (err) {
      handleApiError(err, setError);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPayroll = (payrollId: number, checked: boolean) => {
    if (checked) {
      setSelectedPayrolls(prev => [...prev, payrollId]);
    } else {
      setSelectedPayrolls(prev => prev.filter(id => id !== payrollId));
    }
  };

  const handleSelectAll = (checked: boolean) => {
    setSelectAll(checked);
    if (checked) {
      const currentPagePayrolls = getFilteredData(payrolls)
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
        .map(p => p.id);
      setSelectedPayrolls(currentPagePayrolls);
    } else {
      setSelectedPayrolls([]);
    }
  };

  const handleExport = async () => {
    // This would typically call an API endpoint that generates the export
    // For now, we'll just show a success message
    setSuccess(`Exporting payrolls as ${exportFormat.toUpperCase()}...`);
    setExportDialogOpen(false);
  };

  const handleApprovePayroll = async () => {
    if (!selectedPayroll) return;
    
    setLoading(true);
    try {
      const response = await payrollService.adminApprovePayroll(selectedPayroll.id, approvalForm);
      if (response.success) {
        setSuccess('Payroll approved successfully');
        setApprovalDialogOpen(false);
        loadData();
      } else {
        setError(response.message || 'Failed to approve payroll');
      }
    } catch (err) {
      handleApiError(err, setError);
    } finally {
      setLoading(false);
    }
  };

  const handleProcessPayment = async () => {
    if (!selectedPayroll) return;
    
    setLoading(true);
    try {
      const response = await payrollService.processPayment(selectedPayroll.id, paymentForm);
      if (response.success) {
        setSuccess('Payment processed successfully');
        setPaymentDialogOpen(false);
        loadData();
      } else {
        setError(response.message || 'Failed to process payment');
      }
    } catch (err) {
      handleApiError(err, setError);
    } finally {
      setLoading(false);
    }
  };

  const handleRejectPayroll = async () => {
    if (!selectedPayroll) return;
    
    setLoading(true);
    try {
      const response = await payrollService.rejectPayroll(selectedPayroll.id, rejectForm);
      if (response.success) {
        setSuccess('Payroll rejected');
        setRejectDialogOpen(false);
        loadData();
      } else {
        setError(response.message || 'Failed to reject payroll');
      }
    } catch (err) {
      handleApiError(err, setError);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: PayrollStatus): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (status) {
      case PayrollStatus.Draft: return 'default';
      case PayrollStatus.PendingStaffApproval: return 'warning';
      case PayrollStatus.StaffApproved: return 'info';
      case PayrollStatus.PendingAdminApproval: return 'warning';
      case PayrollStatus.AdminApproved: return 'success';
      case PayrollStatus.Rejected: return 'error';
      case PayrollStatus.PendingPayment: return 'primary';
      case PayrollStatus.Paid: return 'success';
      default: return 'default';
    }
  };

  // Submit draft payroll for admin approval (staff-approve step)
  const handleSubmitPayroll = async (payroll: StaffPayrollDto) => {
    setLoading(true);
    try {
      const response = await payrollService.staffApprovePayroll(payroll.id, 'Submitted for admin review');
      if (response.success) {
        setSuccess('Payroll submitted for admin approval');
        loadData();
      } else {
        setError(response.message || 'Failed to submit payroll');
      }
    } catch (err) {
      handleApiError(err, setError);
    } finally {
      setLoading(false);
    }
  };

  // Download payroll as PDF (backend-generated, matches billing invoice style)
  const handleDownloadPayrollPdf = async (payroll: StaffPayrollDto) => {
    try {
      await payrollService.downloadPdf(payroll.id, payroll.staffName, payroll.payrollStartDate);
    } catch (err) {
      handleApiError(err, setError);
    }
  };

  const handleApproveWork = async (workId: number) => {
    try {
      setLoading(true);
      // Find the work item to include real staff/client identity in the approval record
      const workItem = completedWork.find(w => w.id === workId);
      const request: ApproveCompletedWorkRequest = {
        staffId: workItem?.staffId,
        staffName: workItem?.staffName,
        employeeId: workItem?.employeeId,
        clientId: workItem?.clientId,
        clientName: workItem?.clientName,
        hourlyRate: workItem?.hourlyRate,
      };
      const response = await payrollService.approveCompletedWork(workId, request);
      
      if (response.success) {
        // Update the local state to reflect the approval
        setCompletedWork(prev => prev.map(work => 
          work.id === workId ? { ...work, isApproved: true } : work
        ));
        console.log('Work approved successfully:', workId);
      } else {
        setError(response.errors?.[0] || 'Failed to approve work. Please try again.');
      }
    } catch (error) {
      console.error('Error approving work:', error);
      setError('Failed to approve work. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRejectWork = async (workId: number) => {
    try {
      setLoading(true);
      const response = await payrollService.rejectCompletedWork(workId, 'Work rejected by admin');
      
      if (response.success) {
        // Remove the work item from the list
        setCompletedWork(prev => prev.filter(work => work.id !== workId));
        console.log('Work rejected successfully:', workId);
      } else {
        setError(response.errors?.[0] || 'Failed to reject work. Please try again.');
      }
    } catch (error) {
      console.error('Error rejecting work:', error);
      setError('Failed to reject work. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Shift editing handlers
  const handleEditShift = (shift: CompletedWorkItem) => {
    setSelectedShift(shift);
    setShiftEditDialogOpen(true);
  };

  const handleApproveShift = (shift: CompletedWorkItem) => {
    setSelectedShift(shift);
    setApprovalDialogOpen(true);
  };

  const handleShiftEditSave = async (workId: number, updatedData: EditableShiftData) => {
    try {
      setLoading(true);
      const response = await payrollService.updateShift(workId, updatedData, selectedShift?.shiftDate);
      
      if (response.success) {
        // Re-fetch completed work so the displayed Rate / Total Pay are
        // re-derived from Billing Rates (StaffBaseRate × StaffPay segment %)
        // for the new shift window — never from a stale local formula.
        setShiftEditDialogOpen(false);
        setSelectedShift(null);
        await loadData();
        setSuccess('Shift updated successfully');
      } else {
        setError(response.errors?.[0] || 'Failed to update shift. Please try again.');
      }
    } catch (error) {
      console.error('Error updating shift:', error);
      setError('Failed to update shift. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <PageLayout>
        <Box sx={{ p: 3 }}>
          {/* Page Header with Actions */}
          <PayrollHeader
            selectedPayrolls={selectedPayrolls}
            onBulkActionsClick={() => setBulkDialogOpen(true)}
            onExportClick={() => setExportDialogOpen(true)}
            onRefresh={loadData}
            loading={loading}
          />

          {/* Summary Dashboard */}
          <PayrollSummaryCards
            summary={summary}
            loading={loading}
          />

          {/* Rate-source banner — ensures admins know payroll figures come from
              Billing Rates (StaffBaseRate × StaffPay segment %), not the
              hourly rate stored on the staff profile. */}
          <Alert
            severity="info"
            sx={{ mb: 2 }}
            action={
              <Button
                color="inherit"
                size="small"
                component={RouterLink}
                to="/billing/rates"
              >
                Manage Billing Rates
              </Button>
            }
          >
            <AlertTitle>Pay rates come from Billing Rates</AlertTitle>
            Payroll is generated using each staff member&apos;s <strong>StaffBaseRate</strong> from
            <em> Billing Rates → Staff Rates</em>, multiplied by the matching <strong>StaffPay</strong>
            segment percentage for the day of week and time of day of each shift. The hourly rate
            stored on the staff profile is intentionally not used.
          </Alert>

          {/* Alerts */}
          <PayrollStatusAlerts
            error={error}
            success={success}
            onClearError={() => setError('')}
            onClearSuccess={() => setSuccess('')}
          />

          {/* Filters */}
          <PayrollFilters
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
            dateRange={dateRange}
            onDateRangeChange={setDateRange}
            onClearFilters={() => {
              setSearchTerm('');
              setStatusFilter('');
              setDateRange({ start: null, end: null });
            }}
          />

          {/* Tabs */}
          <Card>
            <PayrollTabs
              activeTab={activeTab}
              onTabChange={setActiveTab}
              totalPayrolls={payrolls.length}
              readyForPayment={readyForPayment.length}
              paidCount={paidPayrolls.length}
              completedWorkCount={completedWork.length}
            />

            <TabPanel value={activeTab} index={0}>
              {/* All Payrolls Summary */}
              {payrolls.length > 0 && (
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Overview</Typography>
                    <Stack direction="row" spacing={4}>
                      <Box>
                        <Typography variant="h4" color="primary">
                          {payrolls.length}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total Payrolls
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="h4" color="success.main">
                          {payrollService.formatCurrency(payrolls.reduce((sum, p) => sum + p.grossPay, 0))}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total Gross Pay
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="h4" color="info.main">
                          {payrolls.reduce((sum, p) => sum + p.totalHours, 0).toFixed(1)}h
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total Hours
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="h4" color="success.main">
                          {readyForPayment.length}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Ready for Payment
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              )}
              
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : payrolls.length === 0 ? (
                <Alert severity="info">
                  No payrolls found. Generate payrolls to get started.
                </Alert>
              ) : (
                <PayrollTable
                  data={payrolls}
                  page={page}
                  rowsPerPage={rowsPerPage}
                  onPageChange={setPage}
                  onRowsPerPageChange={setRowsPerPage}
                  onViewPayroll={handleViewPayroll}
                  onSubmitPayroll={handleSubmitPayroll}
                  onDownloadPdf={handleDownloadPayrollPdf}
                  onSendEmail={handleOpenEmailDialog}
                  onApprovePayroll={(payroll) => {
                    setSelectedPayroll(payroll);
                    setApprovalDialogOpen(true);
                  }}
                  onProcessPayment={(payroll) => {
                    setSelectedPayroll(payroll);
                    setPaymentForm({ paymentMethod: PaymentMethod.DirectDeposit, transactionId: '', paymentReference: '', paymentNotes: '' });
                    setPaymentDialogOpen(true);
                  }}
                  onRejectPayroll={(payroll) => {
                    setSelectedPayroll(payroll);
                    setRejectDialogOpen(true);
                  }}
                  onDeletePayroll={handleDeletePayroll}
                  hasPermission={hasPermission}
                />
              )}
            </TabPanel>

                <TabPanel value={activeTab} index={1}>
                  <Alert severity="success" sx={{ mb: 2 }}>
                    These payrolls are approved and ready for payment processing.
                  </Alert>
                  
                  {/* Ready for Payment Summary */}
                  {readyForPayment.length > 0 && (
                    <Card sx={{ mb: 3 }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>Payment Ready Summary</Typography>
                        <Stack direction="row" spacing={4}>
                          <Box>
                            <Typography variant="h4" color="success.main">
                              {readyForPayment.length}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Ready for Payment
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="h4" color="primary.main">
                              {payrollService.formatCurrency(readyForPayment.reduce((sum, p) => sum + p.netPay, 0))}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Net Payment Total
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="h4" color="info.main">
                              {new Set(readyForPayment.map(p => p.staffId)).size}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Staff Members
                            </Typography>
                          </Box>
                          <Box>
                            <Button
                              variant="contained"
                              color="success"
                              startIcon={<PaymentIcon />}
                              size="small"
                            >
                              Process All Payments
                            </Button>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  )}
                  
                  {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                      <CircularProgress />
                    </Box>
                  ) : readyForPayment.length === 0 ? (
                    <Alert severity="info">
                      No payrolls ready for payment.
                    </Alert>
                  ) : (
                    <PayrollTable
                      data={readyForPayment}
                      page={page}
                      rowsPerPage={rowsPerPage}
                      onPageChange={setPage}
                      onRowsPerPageChange={setRowsPerPage}
                      onViewPayroll={handleViewPayroll}
                      onDownloadPdf={handleDownloadPayrollPdf}
                      onSendEmail={handleOpenEmailDialog}
                      onProcessPayment={(payroll) => {
                        setSelectedPayroll(payroll);
                        setPaymentForm({ paymentMethod: PaymentMethod.DirectDeposit, transactionId: '', paymentReference: '', paymentNotes: '' });
                        setPaymentDialogOpen(true);
                      }}
                      onDeletePayroll={handleDeletePayroll}
                      hasPermission={hasPermission}
                    />
                  )}
                </TabPanel>

                {/* ── Tab 2: Completed Payments ── */}
                <TabPanel value={activeTab} index={2}>
                  <Alert severity="success" sx={{ mb: 2 }}>
                    These payrolls have been fully paid. Transaction records are kept for audit purposes.
                  </Alert>

                  {paidPayrolls.length > 0 && (
                    <Card sx={{ mb: 3 }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>Payments Summary</Typography>
                        <Stack direction="row" spacing={4}>
                          <Box>
                            <Typography variant="h4" color="success.main">{paidPayrolls.length}</Typography>
                            <Typography variant="body2" color="text.secondary">Completed Payments</Typography>
                          </Box>
                          <Box>
                            <Typography variant="h4" color="primary.main">
                              {payrollService.formatCurrency(paidPayrolls.reduce((s, p) => s + p.netPay, 0))}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">Total Net Paid</Typography>
                          </Box>
                          <Box>
                            <Typography variant="h4" color="info.main">
                              {new Set(paidPayrolls.map(p => p.staffId)).size}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">Staff Members</Typography>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  )}

                  {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                      <CircularProgress />
                    </Box>
                  ) : paidPayrolls.length === 0 ? (
                    <Alert severity="info">No completed payments yet.</Alert>
                  ) : (
                    <PayrollTable
                      data={paidPayrolls}
                      page={page}
                      rowsPerPage={rowsPerPage}
                      onPageChange={setPage}
                      onRowsPerPageChange={setRowsPerPage}
                      onViewPayroll={handleViewPayroll}
                      onDownloadPdf={handleDownloadPayrollPdf}
                      onSendEmail={handleOpenEmailDialog}
                      onDeletePayroll={handleDeletePayroll}
                      hasPermission={hasPermission}
                    />
                  )}
                </TabPanel>

                {/* ── Tab 3: Completed Work ── */}
                <TabPanel value={activeTab} index={3}>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    These are completed work items ready for payroll generation.
                  </Alert>
                  
                  {/* Completed Work Summary */}
                  {completedWork.length > 0 && (
                    <Card sx={{ mb: 3 }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>Completed Work Summary</Typography>
                        <Stack direction="row" spacing={4}>
                          <Box>
                            <Typography variant="h4" color="info.main">
                              {completedWork.length}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Completed Shifts
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="h4" color="primary.main">
                              {payrollService.formatCurrency(completedWork.reduce((sum, w) => sum + w.totalPay, 0))}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Total Pay Amount
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="h4" color="success.main">
                              {completedWork.reduce((sum, w) => sum + w.totalHours, 0).toFixed(1)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Total Hours
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="h4" color="warning.main">
                              {new Set(completedWork.map(w => w.staffId)).size}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Staff Members
                            </Typography>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  )}
                  
                  {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                      <CircularProgress />
                    </Box>
                  ) : completedWork.length === 0 ? (
                    <Alert severity="info">
                      No completed work available for payroll generation.
                    </Alert>
                  ) : (
                    <CompletedWorkTable
                      data={completedWork}
                      selectedWork={selectedWork}
                      onSelectWork={(workId) => {
                        setSelectedWork(prev => 
                          prev.includes(workId) 
                            ? prev.filter(id => id !== workId)
                            : [...prev, workId]
                        );
                      }}
                      onSelectAllWork={() => {
                        const selectable = completedWork.filter(w => !w.payrollGenerated);
                        if (selectedWork.length === selectable.length) {
                          setSelectedWork([]);
                        } else {
                          setSelectedWork(selectable.map(work => work.id));
                        }
                      }}
                      onGeneratePayroll={() => {
                        if (selectedWork.length === 0) {
                          setError('Please select work items to generate payroll');
                          return;
                        }
                        setError('');
                        setReviewSelectedIds(selectedWork);
                        setPayrollReviewDialogOpen(true);
                      }}
                      onApproveWork={handleApproveWork}
                      onRejectWork={handleRejectWork}
                      onEditShift={handleEditShift}
                      onApproveShift={handleApproveShift}
                    />
                  )}
                </TabPanel>
          </Card>

          {/* Generate Payrolls Dialog */}
          <Dialog open={generateDialogOpen} onClose={() => setGenerateDialogOpen(false)} maxWidth="lg" fullWidth>
            <DialogTitle>
              <Stack direction="row" alignItems="center" spacing={1}>
                <AddIcon />
                <Typography variant="h6">Generate Payrolls</Typography>
              </Stack>
            </DialogTitle>
            <DialogContent>
              <Box sx={{ pt: 2 }}>
                <Stack spacing={3}>
                  {/* Date Range */}
                  <Stack direction="row" spacing={2}>
                    <DatePicker
                      label="Start Date"
                      value={generateForm.startDate ? new Date(generateForm.startDate) : null}
                      onChange={(date: any) => setGenerateForm(prev => ({ ...prev, startDate: date?.toISOString() || '' }))}
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                    <DatePicker
                      label="End Date"
                      value={generateForm.endDate ? new Date(generateForm.endDate) : null}
                      onChange={(date: any) => setGenerateForm(prev => ({ ...prev, endDate: date?.toISOString() || '' }))}
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  </Stack>

                  {/* Payroll Settings */}
                  <Stack direction="row" spacing={2}>
                    <FormControl fullWidth>
                      <InputLabel>Payroll Frequency</InputLabel>
                      <Select
                        value={generateForm.payrollFrequency}
                        label="Payroll Frequency"
                        onChange={(e) => setGenerateForm(prev => ({ ...prev, payrollFrequency: e.target.value as PayrollFrequency }))}
                      >
                        <MenuItem value={PayrollFrequency.Weekly}>Weekly</MenuItem>
                        <MenuItem value={PayrollFrequency.BiWeekly}>Bi-Weekly</MenuItem>
                        <MenuItem value={PayrollFrequency.Monthly}>Monthly</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField
                      fullWidth
                      label="Overtime Threshold (hours)"
                      type="number"
                      value={generateForm.overtimeThreshold}
                      onChange={(e) => setGenerateForm(prev => ({ ...prev, overtimeThreshold: parseFloat(e.target.value) }))}
                      helperText="Hours after which overtime rates apply"
                    />
                  </Stack>

                  <Stack direction="row" spacing={2}>
                    <TextField
                      fullWidth
                      label="Overtime Multiplier"
                      type="number"
                      value={generateForm.overtimeMultiplier}
                      onChange={(e) => setGenerateForm(prev => ({ ...prev, overtimeMultiplier: parseFloat(e.target.value) }))}
                      inputProps={{ step: 0.1, min: 1, max: 3 }}
                      helperText="e.g., 1.5 for time-and-a-half"
                    />
                    <FormControl fullWidth>
                      <InputLabel>Generation Type</InputLabel>
                      <Select
                        value={generateForm.staffIds?.length ? 'selected' : 'all'}
                        label="Generation Type"
                        onChange={(e) => {
                          if (e.target.value === 'all') {
                            setGenerateForm(prev => ({ ...prev, staffIds: undefined }));
                          }
                        }}
                      >
                        <MenuItem value="all">All Active Staff</MenuItem>
                        <MenuItem value="selected">Selected Staff Only</MenuItem>
                      </Select>
                    </FormControl>
                  </Stack>

                  {/* Staff Selection - Placeholder for future implementation */}
                  <TextField
                    fullWidth
                    label="Staff Selection"
                    placeholder="All active staff members will be included"
                    helperText="Future enhancement: Select specific staff members"
                    disabled
                    value="All Active Staff"
                  />

                  {/* Options */}
                  <Stack spacing={1}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={generateForm.includeOnlyCompletedShifts}
                          onChange={(e) => setGenerateForm(prev => ({ ...prev, includeOnlyCompletedShifts: e.target.checked }))}
                        />
                      }
                      label="Include only completed shifts"
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 4 }}>
                      When enabled, only shifts marked as completed will be included in payroll calculations
                    </Typography>
                  </Stack>

                  {/* Preview Summary */}
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle2" gutterBottom>
                        Generation Preview
                      </Typography>
                      <Stack direction="row" spacing={4}>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Pay Period
                          </Typography>
                          <Typography variant="body2">
                            {generateForm.startDate && generateForm.endDate ? 
                              `${payrollService.formatDate(generateForm.startDate)} - ${payrollService.formatDate(generateForm.endDate)}`
                              : 'Select dates'
                            }
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Frequency
                          </Typography>
                          <Typography variant="body2">
                            {generateForm.payrollFrequency === PayrollFrequency.Weekly ? 'Weekly' :
                             generateForm.payrollFrequency === PayrollFrequency.BiWeekly ? 'Bi-Weekly' : 'Monthly'}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Overtime
                          </Typography>
                          <Typography variant="body2">
                            {generateForm.overtimeThreshold}h @ {generateForm.overtimeMultiplier}x rate
                          </Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                </Stack>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setGenerateDialogOpen(false)}>Cancel</Button>
              <Button 
                onClick={handleGeneratePayrolls} 
                variant="contained" 
                disabled={loading || !generateForm.startDate || !generateForm.endDate}
                startIcon={loading ? <CircularProgress size={20} /> : <AddIcon />}
              >
                {loading ? 'Generating...' : 'Generate Payrolls'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Payroll Review Dialog — used by both "Generate Payroll" and "Generate for Selected" */}
          {(() => {
            // Derive display data from component-level state (no hooks inside)
            const reviewItems = completedWork.filter(w => reviewSelectedIds.includes(w.id));
            const byStaff = reviewItems.reduce<Record<number, { staffName: string; employeeId: string; items: typeof reviewItems }>>((acc, item) => {
              if (!acc[item.staffId]) acc[item.staffId] = { staffName: item.staffName, employeeId: item.employeeId, items: [] };
              acc[item.staffId].items.push(item);
              return acc;
            }, {});
            const startDate = reviewItems.length > 0
              ? reviewItems.reduce((min, w) => w.shiftDate < min ? w.shiftDate : min, reviewItems[0].shiftDate) : '';
            const endDate = reviewItems.length > 0
              ? reviewItems.reduce((max, w) => w.shiftDate > max ? w.shiftDate : max, reviewItems[0].shiftDate) : '';
            const employeeCount = Object.keys(byStaff).length;
            const grandHours = reviewItems.reduce((s, i) => s + i.totalHours, 0);
            const grandPay   = reviewItems.reduce((s, i) => s + i.totalPay, 0);
            const itemsMissingRate = reviewItems.filter(i => (i.hourlyRate ?? 0) <= 0);

            return (
              <Dialog open={payrollReviewDialogOpen} onClose={() => setPayrollReviewDialogOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>Review & Confirm Payroll Generation</DialogTitle>
                <DialogContent dividers>
                  <Stack spacing={3} sx={{ pt: 1 }}>
                    {/* Banner explaining where the rates come from */}
                    <Alert severity="info">
                      <AlertTitle sx={{ mb: 0.5 }}>Pay is computed from Billing Rates</AlertTitle>
                      Each shift below is sliced by day-of-week and time-of-day, then priced as
                      <strong> StaffBaseRate × matching StaffPay segment %</strong> from{' '}
                      <RouterLink to="/billing/rates" style={{ color: 'inherit' }}>Billing Rates</RouterLink>.
                      The staff profile's hourly rate is intentionally NOT used.
                    </Alert>

                    {itemsMissingRate.length > 0 && (
                      <Alert severity="warning">
                        <AlertTitle sx={{ mb: 0.5 }}>
                          {itemsMissingRate.length} shift(s) have no Billing-Rate base configured
                        </AlertTitle>
                        These will be generated with $0 pay. Configure each affected staff
                        member's StaffBaseRate under{' '}
                        <RouterLink to="/billing/rates" style={{ color: 'inherit' }}>Billing Rates → Staff Rates</RouterLink>{' '}
                        before generating, or proceed and edit the payroll afterwards.
                      </Alert>
                    )}

                    {/* Settings row */}
                    <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                      <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel>Pay Frequency</InputLabel>
                        <Select
                          value={reviewFrequency}
                          label="Pay Frequency"
                          onChange={(e) => setReviewFrequency(e.target.value as PayrollFrequency)}
                        >
                          <MenuItem value={PayrollFrequency.Weekly}>Weekly</MenuItem>
                          <MenuItem value={PayrollFrequency.BiWeekly}>Bi-Weekly</MenuItem>
                          <MenuItem value={PayrollFrequency.Monthly}>Monthly</MenuItem>
                        </Select>
                      </FormControl>
                      <Typography variant="body2" color="text.secondary">
                        Period: {startDate ? payrollService.formatDate(startDate) : '—'} → {endDate ? payrollService.formatDate(endDate) : '—'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {employeeCount} employee(s) · {reviewItems.length} shift(s)
                      </Typography>
                    </Stack>

                    {reviewItems.length === 0 && (
                      <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                        No completed works selected. Go to the Completed Work tab and select shifts.
                      </Typography>
                    )}

                    {/* Per-employee work item list */}
                    {Object.entries(byStaff).map(([staffIdStr, group]) => {
                      const staffHours = group.items.reduce((s, i) => s + i.totalHours, 0);
                      const staffPay   = group.items.reduce((s, i) => s + i.totalPay, 0);
                      return (
                        <Box key={staffIdStr}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="subtitle1" fontWeight="bold">
                              {group.staffName}{' '}
                              <Typography component="span" variant="caption" color="text.secondary">
                                ({group.employeeId})
                              </Typography>
                            </Typography>
                            <Typography variant="body2" color="primary.main" fontWeight="bold">
                              {staffHours.toFixed(2)}h · {payrollService.formatCurrency(staffPay)}
                            </Typography>
                          </Box>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell padding="checkbox">
                                  <Checkbox
                                    size="small"
                                    checked={group.items.every(i => reviewSelectedIds.includes(i.id))}
                                    indeterminate={group.items.some(i => reviewSelectedIds.includes(i.id)) && !group.items.every(i => reviewSelectedIds.includes(i.id))}
                                    onChange={() => {
                                      const allIn = group.items.every(i => reviewSelectedIds.includes(i.id));
                                      setReviewSelectedIds(prev =>
                                        allIn
                                          ? prev.filter(id => !group.items.map(i => i.id).includes(id))
                                          : [...new Set([...prev, ...group.items.map(i => i.id)])]
                                      );
                                    }}
                                  />
                                </TableCell>
                                <TableCell>Date</TableCell>
                                <TableCell>Client</TableCell>
                                <TableCell>Clock In</TableCell>
                                <TableCell>Clock Out</TableCell>
                                <TableCell align="right">Hours (Actual)</TableCell>
                                <TableCell align="right">Pay</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {group.items.map(item => (
                                <TableRow key={item.id} sx={!reviewSelectedIds.includes(item.id) ? { opacity: 0.45 } : {}}>
                                  <TableCell padding="checkbox">
                                    <Checkbox
                                      size="small"
                                      checked={reviewSelectedIds.includes(item.id)}
                                      onChange={() => setReviewSelectedIds(prev =>
                                        prev.includes(item.id) ? prev.filter(id => id !== item.id) : [...prev, item.id]
                                      )}
                                    />
                                  </TableCell>
                                  <TableCell>{payrollService.formatDate(item.shiftDate)}</TableCell>
                                  <TableCell>{item.clientName}</TableCell>
                                  <TableCell>
                                    <Typography variant="body2" color={item.actualStart !== item.scheduledStart ? 'success.main' : 'text.secondary'}>
                                      {item.actualStart}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2" color={item.actualEnd !== item.scheduledEnd ? 'success.main' : 'text.secondary'}>
                                      {item.actualEnd}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="right">
                                    <Typography variant="body2" fontWeight="bold">{item.totalHours.toFixed(2)}h</Typography>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      sched: {item.scheduledStart}–{item.scheduledEnd}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="right">
                                    <Typography
                                      variant="body2"
                                      color={item.totalPay > 0 ? 'inherit' : 'error.main'}
                                      title={item.rateSource || ''}
                                    >
                                      {payrollService.formatCurrency(item.totalPay)}
                                    </Typography>
                                    {item.hourlyRate > 0 && (
                                      <Typography variant="caption" color="text.secondary" display="block">
                                        base {payrollService.formatCurrency(item.hourlyRate)}/hr
                                      </Typography>
                                    )}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                          <Divider sx={{ mt: 1 }} />
                        </Box>
                      );
                    })}

                    {/* Grand total */}
                    {reviewItems.length > 0 && (
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                          {reviewItems.length} shifts selected
                        </Typography>
                        <Typography variant="subtitle1" fontWeight="bold">
                          Total: {grandHours.toFixed(2)}h · {payrollService.formatCurrency(grandPay)}
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setPayrollReviewDialogOpen(false)}>Cancel</Button>
                  <Button
                    variant="contained"
                    onClick={handleConfirmGeneratePayrolls}
                    disabled={reviewLoading || reviewSelectedIds.length === 0}
                    startIcon={reviewLoading ? <CircularProgress size={20} /> : <AddIcon />}
                  >
                    {reviewLoading ? 'Generating...' : `Generate ${employeeCount} Payroll(s)`}
                  </Button>
                </DialogActions>
              </Dialog>
            );
          })()}

          {/* View Payroll Dialog — uses the shared PayrollDetailsDialog so the
              Billing-Rate segment breakdown + "rates come from Billing Rates"
              hint are always visible. */}
          <PayrollDetailsDialog
            open={viewDialogOpen}
            onClose={() => setViewDialogOpen(false)}
            payroll={selectedPayroll}
            onDownload={selectedPayroll ? () => handleDownloadPayrollPdf(selectedPayroll) : undefined}
          />

          {/* Approval Dialog */}
          <ApprovalDialog
            open={approvalDialogOpen}
            onClose={() => setApprovalDialogOpen(false)}
            onApprove={handleApprovePayroll}
            loading={loading}
            approvalForm={approvalForm}
            onFormChange={setApprovalForm}
          />

          {/* Payment Processing Dialog */}
          <PaymentDialog
            open={paymentDialogOpen}
            onClose={() => setPaymentDialogOpen(false)}
            onProcessPayment={handleProcessPayment}
            loading={loading}
            paymentForm={paymentForm}
            onFormChange={setPaymentForm}
            payroll={selectedPayroll}
          />

          {/* Send Email Dialog */}
          <Dialog open={emailDialogOpen} onClose={() => setEmailDialogOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>Send Payroll Email</DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Send Payroll #{emailTarget?.id} ({emailTarget?.staffName}) to:
              </Typography>
              <TextField
                autoFocus
                fullWidth
                label="Recipient Email"
                type="email"
                value={emailAddress}
                onChange={(e) => setEmailAddress(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleSendEmail(); }}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setEmailDialogOpen(false)} disabled={emailSending}>Cancel</Button>
              <Button variant="contained" onClick={handleSendEmail} disabled={emailSending}>
                {emailSending ? 'Sending…' : 'Send Email'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Reject Dialog */}
          <RejectDialog
            open={rejectDialogOpen}
            onClose={() => setRejectDialogOpen(false)}
            onReject={handleRejectPayroll}
            loading={loading}
            rejectForm={rejectForm}
            onFormChange={setRejectForm}
          />

        {/* Bulk Operations Dialog */}
        <Dialog open={bulkDialogOpen} onClose={() => setBulkDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>Bulk Operations</DialogTitle>
          <DialogContent>
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Action</InputLabel>
              <Select
                value={bulkForm.action}
                onChange={(e) => setBulkForm(prev => ({ 
                  ...prev, 
                  action: e.target.value as any 
                }))}
                label="Action"
              >
                <MenuItem value="approve">Approve</MenuItem>
                <MenuItem value="reject">Reject</MenuItem>
                <MenuItem value="markPaid">Mark as Paid</MenuItem>
                <MenuItem value="delete">Delete</MenuItem>
              </Select>
            </FormControl>
            
            <Autocomplete
              multiple
              options={payrolls}
              getOptionLabel={(option) => `${option.staffName} - ${option.payrollStartDate} to ${option.payrollEndDate}`}
              value={payrolls.filter(p => selectedPayrolls.includes(p.id))}
              onChange={(_, newValue) => {
                setSelectedPayrolls(newValue.map(p => p.id));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Payrolls"
                  placeholder="Choose payrolls"
                  margin="normal"
                  fullWidth
                />
              )}
            />
            
            {bulkForm.action === 'reject' && (
              <TextField
                fullWidth
                margin="normal"
                label="Rejection Reason"
                value={bulkForm.reason || ''}
                onChange={(e) => setBulkForm(prev => ({ ...prev, reason: e.target.value }))}
                multiline
                rows={3}
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setBulkDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleBulkOperation} 
              variant="contained"
              disabled={selectedPayrolls.length === 0 || !bulkForm.action}
            >
              Apply to {selectedPayrolls.length} payroll(s)
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Payroll Dialog */}
        <EditPayrollDialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          onSave={() => {
            if (selectedPayroll) {
              // Handle edit save - could be implemented later
              setEditDialogOpen(false);
            }
          }}
          loading={loading}
          selectedPayroll={selectedPayroll}
          editForm={editForm}
          onFormChange={setEditForm}
        />

        {/* Weekly Payroll Generation Dialog */}
        <WeeklyPayrollDialog
          open={quickWeeklyDialogOpen}
          onClose={() => setQuickWeeklyDialogOpen(false)}
          onGenerate={handleQuickWeeklyPayrolls}
          loading={loading}
          weeklyForm={weeklyForm}
          onFormChange={setWeeklyForm}
        />

        {/* Export Dialog */}
        <ExportDialog
          open={exportDialogOpen}
          onClose={() => setExportDialogOpen(false)}
          onExport={handleExport}
          loading={loading}
          exportFormat={exportFormat}
          onFormatChange={setExportFormat}
        />

        {/* Shift Edit Dialog */}
        <ShiftEditApprovalDialog
          open={shiftEditDialogOpen}
          onClose={() => {
            setShiftEditDialogOpen(false);
            setSelectedShift(null);
          }}
          workItem={selectedShift}
          onSave={handleShiftEditSave}
          mode="edit"
        />
        </Box>
      </PageLayout>
    </LocalizationProvider>
  );
};

export default PayrollPage;
